import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { type MetricDefinition } from "@/components/admin/XeroMetricInfo";
import XeroPanel, { SCROLLER } from "@/components/admin/XeroPanel";
import { formatNumber, formatWhen } from "@/lib/xeroRunFormat";
import { useXeroOrgName } from "@/hooks/useXeroOrgName";
import {
  XERO_QUOTA_QUERY_KEY,
  getXeroQuota,
  type XeroOrganisationQuota,
} from "@/services/xeroApi";


const STALE_MS = 60_000;


const NEAR_LIMIT = 0.8;

const SOURCE_NOTE: Record<XeroOrganisationQuota["source"], string> = {
  day_limit_header:
    "Read from Xero's own X-DayLimit-Remaining header. This is Xero's accounting of its own rolling window.",
  api_calls:
    "Counted by the Fabric notebook: every request it made, including retries and token rotations. Still a lower bound, because the organisation's budget is shared with any other application connected to it.",
  pages_fetched:
    "Estimated from pages fetched, because nothing better was reported. It undercounts: rate-limit retries, server-error retries, token rotations and empty responses all cost a call and are not counted, and a failed organisation records none at all.",
};

const METRICS: MetricDefinition[] = [
  {
    term: "Used",
    definition:
      "Share of the organisation's Xero call budget spent inside the window. Amber from 80% — well short of the cap, because a backfill can spend the remainder in one go.",
  },
  {
    term: "Calls",
    definition:
      "Calls made to Xero for that organisation in the trailing 24 hours. The window is rolling, not a calendar day: a run at 23:00 is still counted at 01:00, which is why a backfill can be refused the morning after one that looked clean.",
  },
  {
    term: "Remaining",
    definition:
      "Calls left of the 5,000 Xero allows per organisation per rolling 24 hours, across every Xero endpoint rather than journals alone.",
  },
  {
    term: "Source",
    definition:
      "\"From Xero\" means the figure came from Xero's own X-DayLimit-Remaining header and is authoritative. \"Estimated\" means Prism counted its own calls, which is a lower bound — hover the label for what that particular row is missing.",
  },
  {
    term: "Last run",
    definition: "When the most recent run for that organisation started.",
  },
];

function Meter({ row }: { row: XeroOrganisationQuota }) {
  const fraction = Math.min(row.calls_used / row.limit, 1);
  const near = fraction >= NEAR_LIMIT;
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full rounded-full ${near ? "bg-amber-500" : "bg-primary"}`}
          style={{ width: `${Math.max(fraction * 100, fraction > 0 ? 2 : 0)}%` }}
        />
      </div>
      <span className={`tabular-nums ${near ? "text-amber-700" : "text-muted-foreground"}`}>
        {row.percent_used}%
      </span>
    </div>
  );
}


export default function XeroQuotaTable() {
  const orgName = useXeroOrgName();
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: XERO_QUOTA_QUERY_KEY,
    queryFn: getXeroQuota,
    staleTime: STALE_MS,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading the Xero request budget…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          {(error as Error)?.message ?? "Couldn’t load the Xero request budget."}
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const rows = data?.organisations ?? [];
  const limit = data?.limit ?? 5000;

  return (
    <XeroPanel
      title="Request budget"
      blurb={
        <>
          Xero allows {formatNumber(limit)} calls per organisation per rolling 24
          hours, across every endpoint. Counted from{" "}
          {data ? formatWhen(data.window_start) : "—"}.
        </>
      }
      metrics={METRICS}
      onRefresh={() => refetch()}
      isFetching={isFetching}
    >
      {data?.connected === false && (
        <p className="mb-3 text-xs text-muted-foreground">
          Xero is not connected, so organisation names are unavailable. The usage below is
          still what the run log recorded.
        </p>
      )}

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No Xero calls recorded in the last 24 hours.
        </div>
      ) : (
        <TooltipProvider delayDuration={200}>
          <div className={SCROLLER}>
            <Table>
              <TableHeader>
                <TableRow className="sticky top-0 z-10 bg-muted hover:bg-muted">
                  <TableHead>Organisation</TableHead>
                  <TableHead className="w-[150px]">Used</TableHead>
                  <TableHead className="w-[110px] text-right">Calls</TableHead>
                  <TableHead className="w-[110px] text-right">Remaining</TableHead>
                  <TableHead className="w-[100px]">Source</TableHead>
                  <TableHead className="w-[170px]">Last run</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.tenant_id}>
                    <TableCell className="align-top">
                      <div className="font-medium text-foreground">
                        {orgName(row.tenant_id, row.tenant_name)}
                      </div>
                      {row.limit_hits_in_window > 0 && (
                        <p className="mt-1 text-xs text-amber-700">
                          Hit the daily limit {row.limit_hits_in_window}{" "}
                          {row.limit_hits_in_window === 1 ? "time" : "times"} in this window,
                          so the figure below is certainly too low.
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      <Meter row={row} />
                    </TableCell>
                    <TableCell className="align-top text-right tabular-nums text-muted-foreground">
                      {formatNumber(row.calls_used)}
                    </TableCell>
                    <TableCell className="align-top text-right tabular-nums text-muted-foreground">
                      {formatNumber(row.calls_remaining)}
                    </TableCell>
                    <TableCell className="align-top">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className={`cursor-help text-xs underline decoration-dotted ${
                              row.estimated ? "text-amber-700" : "text-muted-foreground"
                            }`}
                          >
                            {row.estimated ? "Estimated" : "From Xero"}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-xs">
                          {SOURCE_NOTE[row.source]}
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell className="align-top text-muted-foreground">
                      {formatWhen(row.last_run_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TooltipProvider>
      )}
    </XeroPanel>
  );
}
